// src/modules/config/components/MenuItemModal/PricingTab.tsx
import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Select, Row, Col, Divider, Typography, Space, Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface PricingTabProps {
  form?: any; // Ant Design form instance
}

export const PricingTab: React.FC<PricingTabProps> = ({ form }) => {
  const [physicalStock, setPhysicalStock] = useState<number>(0);
  const [discrepancy, setDiscrepancy] = useState<number>(0);

  // Получаем значения из формы
  const currentStock = Form.useWatch('current_stock', form) || 0;
  
  // Данные для расчетов
  const totalManufactured = Form.useWatch('total_manufactured', form) || 0;
  const completedQuantity = Form.useWatch('completed_quantity', form) || 0;
  const cartReservedQuantity = Form.useWatch('cart_reserved_quantity', form) || 0;
  const pendingQuantity = Form.useWatch('pending_quantity', form) || 0;
  const confirmedQuantity = Form.useWatch('confirmed_quantity', form) || 0;
  const preparingQuantity = Form.useWatch('preparing_quantity', form) || 0;
  const deliveringQuantity = Form.useWatch('delivering_quantity', form) || 0;

  // Вычисляемые значения
  const reservedTotal = 
    cartReservedQuantity + 
    pendingQuantity + 
    confirmedQuantity + 
    preparingQuantity + 
    deliveringQuantity;
  
  const availableForOrders = currentStock - reservedTotal;

  // Инициализация физического остатка при загрузке данных
  useEffect(() => {
    if (currentStock !== undefined) {
      setPhysicalStock(currentStock);
    }
  }, [currentStock]);

  // Вычисление расхождения
  useEffect(() => {
    const diff = physicalStock - currentStock;
    setDiscrepancy(diff);
  }, [physicalStock, currentStock]);

  // Обработчик изменения физического остатка
  const handlePhysicalStockChange = (value: number | null) => {
    setPhysicalStock(value || 0);
  };

  return (
    <div>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item name="price" label="Цена (₸)" rules={[{ required: true, message: 'Введите цену' }]}>
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              placeholder="0"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="measure_value" label="Количество" rules={[{ required: true, message: 'Введите количество' }]}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="measure_unit" label="Единица измерения" rules={[{ required: true, message: 'Введите единицу' }]}>
            <Select placeholder="Выберите единицу">
              <Select.Option value="шт">шт</Select.Option>
              <Select.Option value="кг">кг</Select.Option>
              <Select.Option value="г">г</Select.Option>
              <Select.Option value="л">л</Select.Option>
              <Select.Option value="мл">мл</Select.Option>
              <Select.Option value="упак">упак</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label="Статус" initialValue="active">
            <Select>
              <Select.Option value="active">Активно</Select.Option>
              <Select.Option value="discontinued">Снято с производства</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      
      <Divider orientation="left">Питательная ценность</Divider>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item name="calories" label="Калории" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" addonAfter="ккал" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="proteins" label="Белки" initialValue={0}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="0" addonAfter="г" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="fats" label="Жиры" initialValue={0}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="0" addonAfter="г" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="carbs" label="Углеводы" initialValue={0}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="0" addonAfter="г" />
          </Form.Item>
        </Col>
      </Row>

      {/* Скрытые поля для хранения данных с бэкенда */}
      <Form.Item name="total_manufactured" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="completed_quantity" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="cart_reserved_quantity" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="pending_quantity" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="confirmed_quantity" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="preparing_quantity" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="delivering_quantity" hidden>
        <InputNumber />
      </Form.Item>

      <Divider orientation="left">Статистика</Divider>
      <div style={{ 
        background: '#f5f5f5', 
        padding: '16px', 
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Text>Произведено всего:</Text>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>
              <Text>
                {totalManufactured}
              </Text>
            </div>
          </Col>
          <Col span={6}>
            <Text>Продано:</Text>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>
              <Text>
                {completedQuantity}
              </Text>
            </div>
          </Col>
          <Col span={6}>
            <Text>Текущий остаток:</Text>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>
              <Text>
                {currentStock}
              </Text>
            </div>
          </Col>
          <Col span={6}>
            <Text>Доступно для заказов:</Text>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>
              <Text>
                {availableForOrders}
              </Text>
            </div>
          </Col>
        </Row>
      </div>

      <Divider orientation="left">Резервы</Divider>
      <div style={{ 
        background: '#fafafa', 
        padding: '16px', 
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <Row gutter={[16, 8]} style={{ marginBottom: '8px' }}>
          <Col span={24}>
            <Text>В резервах: {reservedTotal}</Text>
          </Col>
        </Row>
        
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Text type="secondary">В корзинах: {cartReservedQuantity}</Text>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Text type="secondary">Ожидает оплаты: {pendingQuantity}</Text>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Text type="secondary">Подтверждено: {confirmedQuantity}</Text>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Text type="secondary">Формируется: {preparingQuantity}</Text>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Text type="secondary">В доставке: {deliveringQuantity}</Text>
          </Col>
        </Row>
      </div>

      <Divider orientation="left">Ревизия</Divider>
      <div style={{ 
        background: discrepancy !== 0 ? '#fffbe6' : '#fafafa',
        padding: '16px', 
        borderRadius: '8px',
        border: discrepancy !== 0 ? '1px solid #ffe58f' : '1px solid #d9d9d9'
      }}>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: '8px' }}>
              <Text>Физически на складе:</Text>
            </div>
            <InputNumber
              value={physicalStock}
              onChange={handlePhysicalStockChange}
              min={0}
              style={{ width: '100%' }}
              placeholder="0"
            />
          </Col>
        </Row>

        {discrepancy !== 0 && (
          <Alert
            message={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space>
                  <WarningOutlined style={{ color: '#ff4d4f' }} />
                  <Text style={{ color: '#ff4d4f' }}>
                    Расхождение: {discrepancy > 0 ? '+' : ''}{discrepancy}
                  </Text>
                </Space>
                <Text style={{ fontSize: '13px' }}>
                  Создать корректировку производства: {discrepancy > 0 ? '+' : ''}{discrepancy}
                </Text>
              </Space>
            }
            type="error"
            showIcon={false}
            style={{ marginTop: '16px' }}
          />
        )}
      </div>

      {/* Скрытое поле для current_stock - только для чтения */}
      <Form.Item name="current_stock" hidden>
        <InputNumber />
      </Form.Item>
    </div>
  );
};